import React, { useState, useEffect } from 'react';
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { Space, Popconfirm, message, Tabs, Modal, Spin, Card, Empty } from 'antd';
import { withRouter } from 'react-router-dom/cjs/react-router-dom';
import GlobalButton from '../../../shared/globalStyling/Button';
import JSZip from 'jszip';
import Papa from 'papaparse';
import { cloneDeep } from 'lodash';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import TransactionsDetailsView from './tabs/TransactionsDetailsView';
import SignupsDetailsView from './tabs/SignupsDetailsView';
import LaunchAdvertiser from '../main/components/LaunchAdvertiser';
import UpdateCreatives from './components/UpdateCreatives';
import SessionsDetailsView from './tabs/SessionsDetailsView';
import { LoadingPage } from '../../../shared/LoadingPage';
import { useTagContext } from '../../../../core/components/tags/useTagContext';

const AdvertiserDetailsView = (props) => {
  const {
    advertiser,
    deleteAdvertiser,
    isSelfService,
    currentOrgId,
    filteredLocations,
    filteredAudiences,
    onUpdateAdvertiser,
    hasSignups,
    hasSessions,
    hasTransactions,
    onUpdateCreatives,
    hasTransactionsReport,
    hasSignupsReport,
    hasSessionsReport,
  } = props;
  const [openLaunch, setOpenLaunch] = useState(false);
  const [csvReport, setCsvReport] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [creativesSignups, setCreativesSignups] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [transactionsReportData, setTransactionsReportData] = useState(null);
  const [tabKey, setTabKey] = useState('transaction');

  const setRef = React.useRef(null);
  const setRefSignUps = React.useRef(null);


  const {
    fetchTags,
    tags,
    loadingTags,
    getBasicTagActivity,
    basicTagActivity,
    tagActivityLoading,
    tagActivityError,
    clearBasicTagActivity,
  } = useTagContext();

  const onFetchTags = () => {
    fetchTags({
      variables: {
        where: {
          orgs_some: {
            OR: [
              {
                parentOrg_some: {
                  id: currentOrgId,
                },
              },
              {
                id: currentOrgId,
              },
            ],
          },
          isDisabled: false,
        },
      },
    });
  };

  useEffect(() => {
    onFetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Early return if advertiser data is not loaded yet
  if (!advertiser) {
    return <LoadingPage />;
  }

  const advertiserDetailSummary = {
    name: advertiser.name,
    startDate: advertiser.startDate,
    endDate: advertiser.endDate,
    impressionsTag: advertiser.impressionsTag.id,
    selectedImpresionsTagOrgId: advertiser.impressionsTag.orgs[0].id,
    insertionOrderIds: advertiser.insertionOrderIds,
    attributionFilter: advertiser.attributionFilter,
    transactionsTags: advertiser.transactionsTags.map((t) => {
      const { eventsTarget, trackMesurements } = t;
      return {
        eventsTarget: {
          connect: {
            id: eventsTarget.id,
            orgId: eventsTarget.orgs[0].id,
          },
        },
        trackMesurements: {
          set: trackMesurements,
        },
      };
    }),
    locations: advertiser.locations.map((l) => {
      const { id } = l;
      return {
        connect: [
          {
            id,
          },
        ],
      };
    }),
    selectedMeasurementTags: advertiser.transactionsTags.map((t) => {
      const { eventsTarget, trackMesurements } = t;
      return {
        value: eventsTarget.id,
        orgTag: eventsTarget.orgs[0].id,
        label: eventsTarget.name,
        website: eventsTarget.orgs[0].website,
        isOwned: eventsTarget.orgs[0].id === currentOrgId,
        isAdvertiser: eventsTarget.isAdvertiser,
        advertiser: eventsTarget.orgs[0].name,
        transactions: eventsTarget.transactions,
        pageViews: eventsTarget.pageViews,
        basketItems: eventsTarget.basketItems,
        signups: eventsTarget.signups,
        appId: eventsTarget.eventTags[0].appId,
        selectedImpresionsTagOrgId: eventsTarget.orgs[0].id,
        measuredEvents: trackMesurements,
      };
    }),
    selectedLocations: advertiser.locations,
    campaignFilters: advertiser.campaigns.map((c) => ({
      insertionOrderId: c.insertionOrderId ? c.insertionOrderId : null,
      lineitemIds: c.lineItemIds ? c.lineItemIds : [],
      lineItemName: c.name ? c.name : null,
      advertiserId: c.advertiserId ? c.advertiserId : null,
      impressionsTag: c.impressionsTag ? c.impressionsTag.id : null,
      selectedImpresionsTagOrgId: c.impressionsTag ? c.impressionsTag.orgs[0].id : null,
    })),
  };

  const onDeleteAdvertiser = async () => {
    try {
      await deleteAdvertiser();
      message.success('Advertiser successfully deleted');
    } catch (err) {
      message.error(`Error deleting advertiser: ${err.message}`);
    }
  };

  const handleDownloadPDF = async () => {
    message.success('Starting Charts Download');

    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: 'a4',
    });

    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Text settings
    const labelFontSize = 11;
    const valueFontSize = 14;
    const paddingLeft = 15;

    if ((hasSignupsReport && tabKey === 'signups') || !hasTransactionsReport) {
      // Grid config
      const columns = 2;
      const rows = 2;
      const gutter = 20;
      const sideMargin = 30;

      // Calculate box dimensions
      const totalGutterX = gutter * (columns - 1);
      const totalGutterY = gutter * (rows - 1);
      const boxWidth = (pageWidth - sideMargin * 2 - totalGutterX) / columns;
      const boxHeight = 35; // fixed height box size

      // Calculate vertical space and adjust it slightly above center
      const gridHeight = boxHeight * rows + totalGutterY;
      const topMargin = (pageHeight - gridHeight) / 2 - 105; // inc go up, dec go down border box
      const signupsElement = setRefSignUps.current;
      const canvas = await html2canvas(signupsElement, {
        backgroundColor: '#ffffff',
        willReadFrequently: true,
        useCORS: true,
        allowTaint: true,
      });
      //chart design
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const imgProperties = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const scaledWidth = pdfWidth * 0.9;
      const scaledHeight = (imgProperties.height * scaledWidth) / imgProperties.width;
      const centerX = (pdfWidth - scaledWidth) / 2;
      const centerXCampaignName = (pageWidth - pdf.getTextWidth(advertiser.name)) / 2;
      //values
      const signups = reportData && reportData.summary.signups;
      const impressions = reportData && reportData.summary.impressions;
      const startDate = reportData && reportData.byDates[0].date;
      const endDate = reportData && reportData.byDates[reportData.byDates.length - 1].date;
      if (!signupsElement) {
        console.error('Element not found');
        return;
      }

      if (!imgData || !imgData.startsWith('data:image/jpeg')) {
        console.error('Invalid image data');
        return;
      }

      //image
      pdf.addImage(imgData, 'JPEG', centerX, 195, scaledWidth, scaledHeight);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.text(`Sign Ups Report`, (pageWidth - pdf.getTextWidth('Sign Ups Report')) / 2, 40);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(130, 130, 130);
      pdf.setFontSize(11);
      pdf.text(advertiser.name, centerXCampaignName, 60);

      const labels = [
        { label: 'Total SignUps', value: signups.toLocaleString() },
        {
          label: 'Attributed Impressions',
          value: `${impressions.toLocaleString()}`,
        },
        { label: 'Start Date', value: startDate },
        { label: 'End Date', value: endDate },
      ];

      labels.forEach((item, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);

        const x = sideMargin + col * (boxWidth + gutter);
        const y = topMargin + row * (boxHeight + gutter);

        // Draw rounded box
        pdf.setDrawColor(200, 200, 200);
        pdf.roundedRect(x, y, boxWidth, boxHeight, 6, 6, 'S');

        // Draw label (top)
        pdf.setTextColor(130, 130, 130);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(labelFontSize);
        pdf.text(item.label, x + paddingLeft / 2, y + 17);

        // Draw value
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(valueFontSize);
        pdf.text(String(item.value), x + paddingLeft / 2, y + 27);
      });
    } else if (hasTransactionsReport && tabKey === 'transaction') {
      const element = setRef.current;
      //chart design & pdf structure
      const boxHeight = 35;
      const boxSpacingX = 10; // horizontal spacing
      const boxSpacingY = 10; // vertical spacing
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const scaledWidth = pdfWidth * 0.9; // Scale the width to 80% of the original
      const boxWidth = (pageWidth - 2 * 20 - 2 * boxSpacingX) / 3;
      const centerX = (pdfWidth - scaledWidth) / 2;
      const offsetY = pageHeight / 2 - 135;
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        willReadFrequently: true,
        useCORS: true,
        allowTaint: true,
      });
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const imgProperties = pdf.getImageProperties(imgData);
      const scaledHeight = (imgProperties.height * scaledWidth) / imgProperties.width; // Adjust height proportionally
      //values
      const customers = transactionsReportData && transactionsReportData.summary.customers;
      const transactions = transactionsReportData && transactionsReportData.summary.transactions;
      const revenue = transactionsReportData && transactionsReportData.summary.revenue;
      const impressions = transactionsReportData && transactionsReportData.summary.impressions;
      const startDate = transactionsReportData && transactionsReportData.byDates[0].date;
      const endDate =
        transactionsReportData &&
        transactionsReportData.byDates[transactionsReportData.byDates.length - 1].date;
      const centerXCampaignName = (pageWidth - pdf.getTextWidth(advertiser.name)) / 2;
      if (!element) {
        console.error('Element not found');
        return;
      }

      if (!imgData || !imgData.startsWith('data:image/jpeg')) {
        console.error('Invalid image data');
        return;
      }
      pdf.addImage(imgData, 'JPEG', centerX, 195, scaledWidth, scaledHeight);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text(
        `Transactions Report`,
        (pageWidth - pdf.getTextWidth('Transactions Report')) / 2,
        45
      );
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setFont('helvetica');
      pdf.setTextColor(130, 130, 130);
      pdf.text(advertiser.name, centerXCampaignName, 67);
      const labels = [
        {
          label: 'Customers',
          value: customers.toLocaleString(),
        },
        {
          label: 'Transactions',
          value: transactions.toLocaleString(),
        },
        {
          label: 'Influenced Sales',
          value: `$${revenue.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}`,
        },
        {
          label: 'Attributed Impressions',
          value: impressions.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
        },
        { label: 'Start Date', value: startDate },
        { label: 'End Date', value: endDate },
      ];
      // Draw 3x2 boxes
      labels.forEach((item, index) => {
        const col = index % 3;
        const row = Math.floor(index / 3);
        const x = 20 + col * (boxWidth + boxSpacingX);
        const y = offsetY + row * (boxHeight + boxSpacingY);

        // Border
        pdf.setDrawColor(180); // light gray
        pdf.roundedRect(x, y, boxWidth, boxHeight, 5, 5);

        // Draw label (top)
        pdf.setTextColor(130, 130, 130);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(labelFontSize);
        pdf.text(item.label, x + 10, y + 14);
        // Value
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(valueFontSize);
        pdf.text(String(item.value), x + 10, y + 28);
      });
    }

    pdf.save('Attention Signals.pdf');
    message.success("PDF's downloaded successfully");
  };

  return (
    <React.Fragment>
      <Modal
        title={`Update Advertiser Funnel`}
        visible={openLaunch}
        footer={null}
        width={'75%'}
        onCancel={() => {
          setOpenLaunch(false);
        }}
      >
        <Tabs>
          <Tabs.TabPane key={'details'} tab={'Funnel Details'}>
            <LaunchAdvertiser
              currentOrgId={currentOrgId}
              tags={tags}
              open={openLaunch}
              onCreate={async (values) => {
                setOpenLaunch(false);
                await onUpdateAdvertiser(values);
              }}
              filteredLocations={filteredLocations}
              filteredAudiences={filteredAudiences}
              isSelfService={isSelfService}
              details={advertiserDetailSummary}
              isUpdate={true}
              onFetchTags={onFetchTags}
              loadingTags={loadingTags}
              getBasicTagActivity={getBasicTagActivity}
              basicTagActivity={basicTagActivity}
              tagActivityLoading={tagActivityLoading}
              tagActivityError={tagActivityError}
              clearBasicTagActivity={clearBasicTagActivity}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key={'creatives'} tab={'Creatives'}>
            <UpdateCreatives
              creatives={creatives.concat(creativesSignups)}
              maskedCreatives={advertiser.creatives}
              onUpdate={onUpdateCreatives}
            />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
      <PageHeaderLayout
        title={advertiser.name}
        rightContent={
          <Space>
            {isSelfService && (
              <GlobalButton
                onClick={() => {
                  message.success('Downloading Report PDF');
                  handleDownloadPDF();
                }}
                type="primary"
                disabled={
                  (tabKey === 'transaction' && !hasTransactionsReport) ||
                  (tabKey === 'signups' && !hasSignupsReport) ||
                  (tabKey === 'sessions' && !hasSessionsReport)
                }
              >
                Download PDF
              </GlobalButton>
            )}

            <GlobalButton
              type="primary"
              onClick={() => {
                try {
                  const zip = new JSZip();
                  csvReport.forEach((report) => {
                    const folder = zip.folder(`Advertiser ${advertiser.name} Reports`);
                    let dupFileData = cloneDeep(report.value);
                    folder.file(`${report.name} Report.csv`, Papa.unparse(dupFileData));
                  });

                  zip.generateAsync({ type: 'blob' }).then(
                    (blob) => {
                      saveAs(blob, 'Advertiser');
                      return true;
                    },
                    function (e) {
                      console.log('error', e);
                      return false;
                    }
                  );
                } catch (err) {
                  console.log(err);
                }
              }}
              disabled={!csvReport || csvReport.length === 0}
            >
              Export CSV
            </GlobalButton>
            {isSelfService && (
              <Popconfirm
                title="Are you sure you want to permanently delete this funnel?"
                onConfirm={onDeleteAdvertiser}
                okText="Yes"
                cancelText="No"
              >
                <GlobalButton type="primary">Delete Funnel</GlobalButton>
              </Popconfirm>
            )}
            <GlobalButton onClick={() => setOpenLaunch(!openLaunch)} type="primary">
              Update Funnel
            </GlobalButton>
          </Space>
        }
      />
      <PageContentLayout>
        {!hasTransactions && !hasSignups && !hasSessions ? (
          <Card style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Empty
              description="No data available for tracked measured events."
            />
          </Card>
        ) : (
          <Tabs
            defaultActiveKey="transaction"
            onChange={(key) => {
              setTabKey(key);
            }}
          >
            {hasTransactions ? (
              <Tabs.TabPane key="transaction" tab="Transactions">
                <TransactionsDetailsView
                  setRef={setRef}
                  setReport={setCsvReport}
                  setCreatives={setCreatives}
                  creatives={advertiser.creatives}
                  setTransactionsReportData={setTransactionsReportData}
                  {...props}
                />
              </Tabs.TabPane>
            ) : null}

            {hasSignups ? (
              <Tabs.TabPane key="signups" tab="Sign Ups">
                <SignupsDetailsView
                  setRefSignUps={setRefSignUps}
                  setReport={setCsvReport}
                  creatives={advertiser.creatives}
                  setCreatives={setCreativesSignups}
                  setReportData={setReportData}
                  {...props}
                />
              </Tabs.TabPane>
            ) : null}

            {hasSessions ? (
              <Tabs.TabPane key="sessions" tab="Sessions">
                <SessionsDetailsView
                  setRefSignUps={setRefSignUps}
                  setReport={setCsvReport}
                  creatives={advertiser.creatives}
                  setCreatives={setCreativesSignups}
                  setReportData={setReportData}
                  {...props}
                />
              </Tabs.TabPane>
            ) : null}
          </Tabs>
        )}
      </PageContentLayout>
    </React.Fragment>
  );
};

export default withRouter(AdvertiserDetailsView);
